<template>
    <div>
        <div class="row" v-loading="loading" data-test="postal-code-city">
            <div :class="postalCodeCol">
                <Field
                    class="PostalCodeCity__postal-code"
                    :name="postalCodeName"
                    :error="postalCodeInputError"
                    :label="postalCodeLabel"
                    :required="required"
                >
                    <template v-slot:label>
                        <label :for="postalCodeName" class="form-control-label">
                            <slot name="postal-code-label">
                                {{ postalCodeLabel }}
                                <span v-if="required" class="text-danger">*</span>
                            </slot>
                        </label>
                    </template>
                    <template v-slot:default="{ classes }">
                        <input v-model="address.postalCode"
                            type="text"
                            class="form-control"
                            :class="classes"
                            :name="postalCodeName"
                            autocomplete="new-password"
                            maxlength="5"
                            @input="handlePostalCodeInput"
                            @focus="handlePostalCodeFocus"
                            @blur="handlePostalCodeBlur"
                            ref="postalCodeInput"
                        >
                    </template>
                </Field>
            </div>

            <div :class="cityCol">
                <Field :name="cityName" :label="cityLabel" :error="cityInputError" v-slot="{ classes }">
                    <template v-if="cities.length > 1">
                        <select v-model="address.city" class="form-control" :class="classes" :name="cityName" :id="cityName">
                            <template v-for="cityOption in cities">
                                <option :value="cityOption" :key="cityOption">{{ cityOption }}</option>
                            </template>
                        </select>
                    </template>
                    <template v-else>
                        <input class="form-control disabled" :class="classes" type="text" readonly :name="cityName" :value="address.city">
                    </template>
                </Field>
            </div>
        </div>
        <template v-if="!noCedex">
            <div v-show="showEditCedex" class="row">
                <div class="col-sm-4 col-12" style="min-width: 9rem">
                    <Field :name="cedexName" :label="cedexLabel" :error="cedexError" v-slot="{ classes }">
                        <input :value="address.cedex" class="form-control" :class="classes" :name="cedexName" maxlength="5" autocomplete="new-password"
                            @input="handleCedexInput" @focus="handleCedexFocus" @blur="handleCedexBlur">
                    </Field>
                </div>
                <div class="col-sm-3 col-6" style="min-width: 10rem">
                    <Field :name="cedexNumberName" :label="cedexNumberLabel" :error="cedexNumberError" v-slot="{ classes }">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text">CEDEX</span>
                            </div>
                            <input v-model="address.cedexNumber" class="form-control" :name="cedexNumberName" :class="classes" maxlength="2" autocomplete="new-password">
                        </div>
                    </Field>
                </div>
                <div class="col align-self-end">
                    <div class="form-group">
                        <div>
                            <button type="button" class="btn btn-secondary btn-sm" @click.prevent="handleHideCedex">supprimer le cedex</button>
                        </div>
                    </div>
                </div>
            </div>
            <div v-show="!showEditCedex">
                <span class="d-inline-block mb-3" ref="openBtn">
                    <button type="button" class="btn btn-secondary btn-sm" :disabled="!isValid" @click.prevent="handleShowCedex" data-test="edit-cedex" :data-testid="cedexName">préciser un cedex</button>
                </span>
            </div>
        </template>
        <b-tooltip :disabled="isValid" placement="bottom" :target="()=>$refs.openBtn">
            Remplissez le Code&nbsp;Postal avant le Cedex
        </b-tooltip>
        <b-tooltip :show.sync="showNoCitiesFoundTooltip" triggers="" placement="bottom"  :target="()=>$refs.postalCodeInput">
            Ce code postal ne correspond à aucune ville : attention de ne pas indiquer ici un code Cedex
        </b-tooltip>
    </div>
</template>

<script>
import axios from 'axios';
import Field from './Field';
import {BTooltip} from 'bootstrap-vue';

// cf: https://geo.api.gouv.fr/docs/communes
    export default {
        components: {
            Field,
            BTooltip,
        },
        props: {
            value: {
                type:Object,
                default:()=>({
                    postalCode: null,
                    city: null,
                    cedex: null,
                    cedexNumber: null
                })
            },
            cityName: {
                type: String,
                default: 'city'
            },
            cityError: String,
            cityLabel: {
                type: String,
                default: 'Ville'
            },
            cityCol:{
                type: String,
                default: 'col-8'
            },

            postalCodeError: String,
            postalCodeName: {
                type: String,
                default: 'postal_code'
            },
            postalCodeLabel: {
                type: String,
                default: 'Code postal'
            },
            postalCodeCol: {
                type:String,
                default: 'col-4'
            },

            cedexName: {
                type:String,
                default: 'cedex'
            },
            cedexError:String,
            cedexLabel: {
                type:String,
                default: 'Cedex'
            },

            cedexNumberName: {
                type:String,
                default: 'cedex_number'
            },
            cedexNumberError: String,
            cedexNumberLabel: {
                type:String,
                default: 'Numéro Cedex'
            },
            noCedex: Boolean,
            required: Boolean
        },
        data() {
            return {
                address: {
                    postalCode: this.value.postalCode,
                    city: this.value.city,
                    cedex: this.value.cedex,
                    cedexNumber: this.value.cedexNumber,
                },

                results: null,
                loading: false,
                showSelect: false,
                postalCodeInputError: this.postalCodeError,
                cityInputError: this.cityError,

                showEditCedex: !!(this.value.cedex || this.value.cedexNumber),
                showNoCitiesFoundTooltip: false
            }
        },
        watch: {
            address: {
                handler: 'handleChanged',
                deep: true,
            },
            value(value) {
                this.address = { ...value };
                this.valueChanged = true;
            },
            postalCodeError(error) {
                this.postalCodeInputError = error;
            },
            cityError(error) {
                this.cityInputError = error;
            },
        },
        computed: {
            cities() {
                return (this.results||[]).map(({ nom }) => nom);
            },
            isValid() {
                return (this.address.postalCode||'').length === 5;
            },
            cedexPrefix() {
                return (this.address.postalCode||'').substring(0, 2);
            }
        },
        methods: {
            get({ assign=true }={}) {
                this.loading = true;
                this.postalCodeInputError = '';
                axios.get(`https://geo.api.gouv.fr/communes?codePostal=${this.address.postalCode}`)
                    .then(({ data:results }) => {
                        this.cityInputError = '';
                        this.setResults(results);
                        if(assign || !this.city) {
                            this.address.city = this.cities[0];
                        }
                    })
                    .catch(()=>{
                        this.postalCodeInputError = 'Erreur réseau';
                    })
                    .finally(()=>{
                        this.loading = false;
                    })
            },
            handleChanged() {
                if(this.valueChanged) {
                    this.valueChanged = false;
                    return;
                }
                this.$emit('input', {
                    ...this.address,
                });
            },
            handlePostalCodeInput() {
                if(this.isValid) {
                    this.get();
                } else {
                    this.results = null;
                    this.address.city = null;
                }
                this.showNoCitiesFoundTooltip = false;
                this.handleHideCedex();
            },
            handlePostalCodeFocus() {
                if(this.isValid && !this.results) {
                    this.get({ assign: false });
                }
            },
            handlePostalCodeBlur() {
                if(!this.isValid) {
                    this.postalCodeInputError = 'Code postal invalide';
                }
            },
            handleHideCedex() {
                this.showEditCedex = false;
                this.address.cedex = null;
                this.address.cedexNumber = null
            },
            handleShowCedex() {
                this.showEditCedex = true;
            },
            handleCedexInput(e) {
                if(e.target.value.substring(0,2) !== this.cedexPrefix) {
                    e.target.value = this.cedexPrefix;
                }
                this.address.cedex = e.target.value;
            },
            handleCedexFocus() {
                if(!this.address.cedex) {
                    this.address.cedex = this.cedexPrefix;
                }
            },
            handleCedexBlur() {
                if(this.address.cedex.length === 2) {
                    this.address.cedex = ''
                }
            },

            setResults(results) {
                this.results = results || [];
                if(!this.results.length) {
                    this.showNoCitiesFoundTooltip = true;
                }
            }
        }
    }
</script>
